import type { FunctionComponent } from 'react';

import { BkAccount } from '@rbilabs/universal-components/themes/bk/icons/BKAccount';
import { BkCode } from '@rbilabs/universal-components/themes/bk/icons/BKCode';
import { BkCrown } from '@rbilabs/universal-components/themes/bk/icons/BKCrown';
import { BkCrownPass } from '@rbilabs/universal-components/themes/bk/icons/BKCrownPass';
import { BkDaytime } from '@rbilabs/universal-components/themes/bk/icons/BKDaytime';
import { BkOffers } from '@rbilabs/universal-components/themes/bk/icons/BKOffers';
import { BkOrder } from '@rbilabs/universal-components/themes/bk/icons/BKOrder';
import { BkRewards } from '@rbilabs/universal-components/themes/bk/icons/BKRewards';
import { BkSaving } from '@rbilabs/universal-components/themes/bk/icons/BKSaving';
import { BkSunrise } from '@rbilabs/universal-components/themes/bk/icons/BKSunrise';
import {
  BkUpsellPlus,
  BkUpsellPlusV2,
} from '@rbilabs/universal-components/themes/bk/icons/BKUpsellPlus';

import { BkLogoPrimary } from '../../themes/bk/logos/BKLogoPrimary';
import { BkLogoRoyalPerks } from '../../themes/bk/logos/BKLogoRoyalPerks';
import { BkLogoRoyalPerksFR } from '../../themes/bk/logos/BKLogoRoyalPerksFR';
import { BkLogoSecondary } from '../../themes/bk/logos/BKLogoSecondary';
import { AddCard } from '../../themes/shared/icons/AddCard';
import { AmazonPay } from '../../themes/shared/icons/AmazonPay';
import { Amex } from '../../themes/shared/icons/Amex';
import { ApplePayMark } from '../../themes/shared/icons/ApplePayMark';
import { Applepay } from '../../themes/shared/icons/Applepay';
import { ApplepayNoBorder } from '../../themes/shared/icons/ApplepayNoBorder';
import { Back } from '../../themes/shared/icons/Back';
import { CalendarToday } from '../../themes/shared/icons/CalendarToday';
import { Cash } from '../../themes/shared/icons/Cash';
import { CashApp } from '../../themes/shared/icons/CashApp';
import { CashAppLogoMark } from '../../themes/shared/icons/CashAppLogoMark';
import { CashAppNoBorder } from '../../themes/shared/icons/CashAppNoBorder';
import { CashAppWhiteBorder } from '../../themes/shared/icons/CashAppWhiteBorder';
import { CashNoBorder } from '../../themes/shared/icons/CashNoBorder';
import { CheckFill } from '../../themes/shared/icons/CheckFill';
import { CheckMark } from '../../themes/shared/icons/CheckMark';
import { CheckMarkGreen } from '../../themes/shared/icons/CheckMarkGreen';
import { CheckMarkWhite } from '../../themes/shared/icons/CheckMarkWhite';
import { CrownCreditCard } from '../../themes/shared/icons/CrownCreditCard';
import { CrownCreditCardNoBorder } from '../../themes/shared/icons/CrownCreditCardNoBorder';
import { Diners } from '../../themes/shared/icons/Diners';
import { DinersNoBorder } from '../../themes/shared/icons/DinersNoBorder';
import { Discover } from '../../themes/shared/icons/Discover';
import { DiscoverNoBorder } from '../../themes/shared/icons/DiscoverNoBorder';
import { DriveThru } from '../../themes/shared/icons/DriveThru';
import { DuplicateItem } from '../../themes/shared/icons/DuplicateItem';
import { EmptyCreditCard } from '../../themes/shared/icons/EmptyCreditCard';
import { Error } from '../../themes/shared/icons/Error';
import { Filters } from '../../themes/shared/icons/Filters';
import { GPayMark } from '../../themes/shared/icons/GPayMark';
import { GiftCard } from '../../themes/shared/icons/GiftCard';
import { GiftCardNoBorder } from '../../themes/shared/icons/GiftCardNoBorder';
import { GooglePay } from '../../themes/shared/icons/GooglePay';
import { GooglePayBrand } from '../../themes/shared/icons/GooglePayBrand';
import { GooglePayNoBorder } from '../../themes/shared/icons/GooglePayNoBorder';
import { HotelClass } from '../../themes/shared/icons/HotelClass';
import { Instagram } from '../../themes/shared/icons/Instagram';
import { Jcb } from '../../themes/shared/icons/Jcb';
import { JcbNoBorder } from '../../themes/shared/icons/JcbNoBorder';
import { Lock } from '../../themes/shared/icons/Lock';
import { MapOutline } from '../../themes/shared/icons/MapOutline';
import { Mastercard } from '../../themes/shared/icons/Mastercard';
import { MastercardNoBorder } from '../../themes/shared/icons/MastercardNoBorder';
import { Masterpass } from '../../themes/shared/icons/Masterpass';
import { Mobile } from '../../themes/shared/icons/Mobile';
import { MobileOrderDriveThru } from '../../themes/shared/icons/MobileOrderDriveThru';
import { Next } from '../../themes/shared/icons/Next';
import { PayPal } from '../../themes/shared/icons/PayPal';
import { PayPalLogo } from '../../themes/shared/icons/PayPalLogo';
import { PayPalNoBorder } from '../../themes/shared/icons/PayPalNoBorder';
import { PaymentMethods } from '../../themes/shared/icons/PaymentMethod';
import { Remove } from '../../themes/shared/icons/Remove';
import { Search } from '../../themes/shared/icons/Search';
import { SmallCreditCardWhite } from '../../themes/shared/icons/SmallCreditCardWhite';
import { SmallGiftCardWhite } from '../../themes/shared/icons/SmallGiftCardWhite';
import { SodiumFlag } from '../../themes/shared/icons/SodiumFlag';
import { Tiktok } from '../../themes/shared/icons/Tiktok';
import { Twitter } from '../../themes/shared/icons/Twitter';
import { Venmo } from '../../themes/shared/icons/Venmo';
import { VenmoLogo } from '../../themes/shared/icons/VenmoLogo';
import { VenmoNoBorder } from '../../themes/shared/icons/VenmoNoBorder';
import { Visa } from '../../themes/shared/icons/Visa';
import { VisaNoBorder } from '../../themes/shared/icons/VisaNoBorder';
import { WhiteEmptyCreditCard } from '../../themes/shared/icons/WhiteEmptyCreditCard';
import { Work } from '../../themes/shared/icons/Work';
import { Youtube } from '../../themes/shared/icons/Youtube';

import type { SvgProps } from './types';

/**
 * Static registry for SVGs resolved by string key (Icon custom:*, BrandLogo variants).
 * Replaces the former registerSVG / svgLibrary side-effect registration.
 */
export const svgRegistry = (<T extends Record<string, FunctionComponent<SvgProps>>>(r: T) => r)({
  AddCard,
  AmazonPay,
  Amex,
  Applepay,
  ApplePayMark,
  ApplepayNoBorder,
  Back,
  BkAccount,
  BkCode,
  BkCrown,
  BkCrownPass,
  BkDaytime,
  BkLogoPrimary,
  BkLogoRoyalPerks,
  BkLogoRoyalPerksFR,
  BkLogoSecondary,
  BkOffers,
  BkOrder,
  BkRewards,
  BkSaving,
  BkSunrise,
  BkUpsellPlus,
  BkUpsellPlusV2,
  CalendarToday,
  Cash,
  CashApp,
  CashAppLogoMark,
  CashAppNoBorder,
  CashAppWhiteBorder,
  CashNoBorder,
  CheckFill,
  CheckMark,
  CheckMarkGreen,
  CheckMarkWhite,
  CrownCreditCard,
  CrownCreditCardNoBorder,
  Diners,
  DinersNoBorder,
  Discover,
  DiscoverNoBorder,
  DriveThru,
  DuplicateItem,
  EmptyCreditCard,
  Error,
  Filters,
  GiftCard,
  GiftCardNoBorder,
  GooglePay,
  GooglePayBrand,
  GooglePayNoBorder,
  GPayMark,
  HotelClass,
  Instagram,
  Jcb,
  JcbNoBorder,
  Lock,
  MapOutline,
  Mastercard,
  MastercardNoBorder,
  Masterpass,
  Mobile,
  MobileOrderDriveThru,
  Next,
  PaymentMethods,
  PayPal,
  PayPalLogo,
  PayPalNoBorder,
  Remove,
  Search,
  SmallCreditCardWhite,
  SmallGiftCardWhite,
  SodiumFlag,
  Tiktok,
  Twitter,
  Venmo,
  VenmoLogo,
  VenmoNoBorder,
  Visa,
  VisaNoBorder,
  WhiteEmptyCreditCard,
  Work,
  Youtube,
});
